import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import { useState, useEffect } from 'react';
import { isTokenExpired } from '../../../utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5%;
  margin: 20px 0;

  @media (max-width: 1200px) {
    padding: 1.25%;
    margin: 18px 0;
  }

  @media (max-width: 992px) {
    padding: 1%;
    margin: 16px 0;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 14px 0;
  }

  @media (max-width: 576px) {
    padding: 0.75rem;
    margin: 12px 0;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    margin: 10px 0;
  }

  @media (max-width: 360px) {
    padding: 0.25rem;
    margin: 8px 0;
  }
`;

const GoldBanner = styled.div`
  background-color: #d3a94e;
  width: 90%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 400px;
  border-radius: 8px;

  @media (max-width: 1400px) {
    width: 92%;
    gap: 300px;
  }

  @media (max-width: 1200px) {
    width: 94%;
    gap: 250px;
    height: 90px;
  }

  @media (max-width: 992px) {
    width: 96%;
    gap: 200px;
    height: 85px;
  }

  @media (max-width: 768px) {
    height: auto;
    gap: 1.5rem;
    padding: 1rem 0.5rem;
    width: 95%;
  }

  @media (max-width: 576px) {
    width: 96%;
    padding: 0.7rem 0.3rem;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    width: 98%;
    padding: 0.5rem 0.3rem;
    gap: 1rem;
    height: 60px;
  }

  @media (max-width: 360px) {
    width: 100%;
    padding: 1.25rem 0.75rem;
    gap: 0.75rem;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    margin: 0 0 5px 0;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 0.05em;
  }
  h2 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 500;
    letter-spacing: 0.05em;
  }

  @media (max-width: 1200px) {
    h3 {
      font-size: 0.85rem;
      margin-bottom: 4px;
    }
    h2 {
      font-size: 1.3rem;
    }
  }

  @media (max-width: 992px) {
    h3 {
      font-size: 0.8rem;
      margin-bottom: 3px;
    }
    h2 {
      font-size: 1.2rem;
    }
  }

  @media (max-width: 768px) {
    h3 {
      font-size: 0.8rem;
      margin-bottom: 0.25rem;
      letter-spacing: 0.03em;
    }
    h2 {
      font-size: 1.1rem;
      letter-spacing: 0.03em;
    }
  }

  @media (max-width: 576px) {
    h3 {
      font-size: 0.75rem;
      margin-bottom: 0.2rem;
    }
    h2 {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    h3 {
      font-size: 0.5rem;
      letter-spacing: 0.01em;
      margin-bottom: 0.15rem;
    }
    h2 {
      font-size: 0.7rem;
      letter-spacing: 0.01em;
    }
  }

  @media (max-width: 360px) {
    h3 {
      font-size: 0.55rem;
      margin-bottom: 0.15rem;
    }
    h2 {
      font-size: 0.75rem;
    }
  }
`;

const getResponsiveButtonStyles = () => {
  const width = window.innerWidth;

  if (width <= 360) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.55rem',
      padding: '0.2rem 0.4rem',
      height: 'auto',
    };
  } else if (width <= 480) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.6rem',
      padding: '0.25rem 0.5rem',
      height: 'auto',
    };
  } else if (width <= 576) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.7rem',
      padding: '0.3rem 0.6rem',
      height: 'auto',
    };
  } else if (width <= 768) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.75rem',
      padding: '0.35rem 0.7rem',
      height: 'auto',
    };
  } else if (width <= 992) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.8rem',
      padding: '0.4rem 0.8rem',
      height: 'auto',
    };
  } else if (width <= 1200) {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.85rem',
      padding: '0.45rem 0.9rem',
      height: 'auto',
    };
  } else {
    return {
      backgroundColor: '#191817',
      color: 'white',
      fontSize: '0.9rem',
      padding: '0.5rem 1rem',
      height: 'auto',
    };
  }
};

const RequestQuoteSection = () => {
  const navigate = useNavigate();
  const [buttonStyles, setButtonStyles] = useState(getResponsiveButtonStyles());

  useEffect(() => {
    const handleResize = () => {
      setButtonStyles(getResponsiveButtonStyles());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleButtonClick = () => {
    const token = localStorage.getItem('token');

    // Check if user is logged in
    if (!token || isTokenExpired(token)) {
      // Redirect to login page if not logged in
      navigate('/login');
    } else {
      // Navigate to contact us page if logged in
      navigate('/contactus');
    }
  };

  return (
    <Container>
      <GoldBanner>
        <TextContainer>
          <h3>Tell us what you need, we&apos;ll price it right for you</h3>
          <h2>Get a personalized quote just for you</h2>
        </TextContainer>
        <Button onClick={handleButtonClick} style={buttonStyles}>
          Request a Quote
        </Button>
      </GoldBanner>
    </Container>
  );
};

export default RequestQuoteSection;
